/**
 * DO NOT CONTACT, ON THE SCREEN (ops #44, step two).
 *
 * The owner asked on 2026-09-17 for decent tracking of who, when and why set do not contact, for
 * texts, calls, email or all of them, on a number, an address or a person. The register has refused
 * every message ops composes since step one; this is the half a person reads and writes:
 *
 *   A PILL PER MARK THAT HOLDS, "Do not contact: texts, calls", on the person's card, on the
 *   enquiries list and on the calls list, so nobody rings or writes to somebody who asked not to be.
 *   Gold, never coral: a mark is a decision somebody already recorded, not a gap anybody has to close.
 *
 *   A LINE PER MARK under the person, "Do not text this number. She asked us, September 12, 2026,
 *   recorded by Victoria.", and the controls beside it: anybody in the office marks her, and an owner
 *   or a manager lifts a mark (the owner, 2026-10-15), each with a typed reason, the day she said it
 *   and a note in her words. A lift is a row of its own and the mark stays on the record; the history
 *   under them shows every row, newest first, with who, when, why and the note, which nothing else on
 *   any screen carries.
 *
 *   WHAT A LIFT RELEASES IS SAID BEFORE IT IS WRITTEN. A lift opens its channel, so it releases every
 *   set still standing there beneath the mark it names: the door refuses it and names them, the dialog
 *   shows them, and they are lifted together or left all. And a stop on texts the phone system was
 *   told of may stay refused there after a lift here, since nothing tells it otherwise yet
 *   (`faithfully-kept-phone#26`): the dialog says so before, and the door's answer after, rather than
 *   promise she may be texted again.
 *
 * HIDING THE LIFT FROM ANYBODY BUT AN OWNER OR A MANAGER IS UX, NEVER THE CONTROL:
 * `api/src/routes/do-not-contact.ts` refuses a lift from anybody else whatever this draws (binding
 * rule 6), and both read `MARK_LIFTING_ROLES`.
 *
 * AND A NUMBER MARKED FOR CALLS IS WRITTEN OUT, NOT OFFERED (`CallLink` with `marked`): no `tel:`
 * link, the words "Do not call" in front of it. That is all ops can do for voice, because a call is
 * placed from a handset outside the software.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  LIFT_REASONS,
  OFFICE_MARK_REASONS,
  SUPPRESSION_SCOPES,
  type DoNotContactReason,
  type DoNotContactTarget,
  type LiftReason,
  type OfficeMarkReason,
  type SuppressionScope,
} from '@fk/shared';
import {
  ApiError,
  beneathOf,
  liftMark,
  markHistory,
  setMark,
  type BeneathMark,
  type ContactRow,
  type HoldingMark,
  type LiftAnswer,
  type MarkChannel,
  type MarkHistoryRow,
  type MarksHeld,
  type MarkTarget,
} from '../api.ts';
import { capturedAtFor, isAheadOfToday } from '../consent.ts';
import { dayOf, formatDay, formatPhone, orNull } from '../format.ts';
import {
  DO_NOT_CONTACT_DOOR_LABELS,
  DO_NOT_CONTACT_REASON_LABELS,
  MARK_SCOPE_LABELS,
} from '../labels.ts';
import { Pill } from './AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from './Field.tsx';
import { ErrorNotice } from './Notice.tsx';

/** The three channels a mark holds on, in the order every line names them. */
const MARK_CHANNELS: readonly MarkChannel[] = ['sms', 'email', 'call'];

const CHANNEL_NOUN: Record<MarkChannel, string> = { sms: 'texts', email: 'emails', call: 'calls' };
const CHANNEL_VERB: Record<MarkChannel, string> = { sms: 'text', email: 'email', call: 'call' };

/** Who a mark is on, as the sentence after "Do not text" says it. */
const MARKED: Record<DoNotContactTarget, string> = {
  contact: 'them',
  phone: 'this number',
  email: 'this address',
};

/** One mark that holds, and every channel it holds on. */
export interface MarkGroup {
  readonly mark: HoldingMark;
  readonly channels: readonly MarkChannel[];
}

/** The marks that hold, one group per mark: a mark on every channel is one pill, not three. */
export function markGroups(marks: MarksHeld | null | undefined): MarkGroup[] {
  if (!marks) return [];
  const groups = new Map<string, { mark: HoldingMark; channels: MarkChannel[] }>();
  for (const channel of MARK_CHANNELS) {
    const mark = marks[channel];
    if (!mark) continue;
    const group = groups.get(mark.id) ?? { mark, channels: [] };
    group.channels.push(channel);
    groups.set(mark.id, group);
  }
  return [...groups.values()];
}

/** "text", "text or call", "text, email or call". */
function orList(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`;
}

/** The pill: "Do not contact: texts, calls". */
export function markPillWords(group: MarkGroup): string {
  return `Do not contact: ${group.channels.map((channel) => CHANNEL_NOUN[channel]).join(', ')}`;
}

/** Who wrote it down: the person by name where the read carries one, else the door it came through. */
function whoWords(name: string | null | undefined, door: HoldingMark['door']): string {
  return name ? `recorded by ${name}` : DO_NOT_CONTACT_DOOR_LABELS[door];
}

/** The line: "Do not text this number. She asked us, September 12, 2026, recorded by Victoria." */
export function markLine(group: MarkGroup): string {
  const { mark, channels } = group;
  const doNot = `Do not ${orList(channels.map((channel) => CHANNEL_VERB[channel]))} ${MARKED[mark.target]}.`;
  const day = formatDay(mark.saidOn) ?? mark.saidOn;
  return `${doNot} ${DO_NOT_CONTACT_REASON_LABELS[mark.reason]}, ${day}, ${whoWords(mark.recordedByName, mark.door)}.`;
}

/** A pill for every mark that holds, and nothing where none does or the read did not say. */
export function MarkPills({ marks }: { marks: MarksHeld | null | undefined }) {
  return (
    <>
      {markGroups(marks).map((group) => (
        <Pill key={group.mark.id} tone="gold">
          {markPillWords(group)}
        </Pill>
      ))}
    </>
  );
}

type Step =
  | { readonly kind: 'shut' }
  | { readonly kind: 'set' }
  | { readonly kind: 'lift'; readonly group: MarkGroup };

/**
 * ONE PERSON'S MARKS: the line for each that holds, Mark and Lift, and the history. `canLift` is an
 * owner or a manager, as the server decides it anyway. `held` is the stop in her own words waiting
 * on her number (ops #33), drawn above the marks by the page that has it, since a hold is decided
 * before anything here is lifted.
 */
export function DoNotContactPanel({
  contact,
  canLift,
  today,
  onDone,
  held = null,
}: {
  contact: ContactRow;
  canLift: boolean;
  today: string;
  onDone: (sentence: string) => void;
  held?: ReactNode;
}) {
  const marks = contact.messaging?.doNotContact;
  const groups = markGroups(marks);
  const [step, setStep] = useState<Step>({ kind: 'shut' });
  // Read again after every write here, so the history never shows the register as it was.
  const [written, setWritten] = useState(0);

  const done = (sentence: string) => {
    setStep({ kind: 'shut' });
    setWritten((n) => n + 1);
    onDone(sentence);
  };

  return (
    <div className="mt-3 border-t border-navy/10 pt-3">
      <p className="text-xs tracking-wide text-navy/55 uppercase">Do not contact</p>
      {held ? <div className="mt-1 mb-2">{held}</div> : null}
      {marks === undefined ? null : groups.length === 0 ? (
        <p className="mt-1 text-sm text-navy/70">
          No mark holds on them, their number or their address.
        </p>
      ) : (
        <ul className="mt-1 flex flex-col gap-2">
          {groups.map((group) => (
            <li key={group.mark.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="gold">{markPillWords(group)}</Pill>
              </div>
              <p className="text-sm">{markLine(group)}</p>
              {canLift && step.kind === 'shut' ? (
                <div>
                  <Button tone="quiet" onClick={() => setStep({ kind: 'lift', group })}>
                    Lift
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {step.kind === 'shut' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button tone="quiet" onClick={() => setStep({ kind: 'set' })}>
            Mark do not contact
          </Button>
        </div>
      ) : null}

      {step.kind === 'set' ? (
        <SetMark
          contact={contact}
          today={today}
          onDone={done}
          onCancel={() => setStep({ kind: 'shut' })}
        />
      ) : null}

      {step.kind === 'lift' ? (
        <LiftMark
          contact={contact}
          group={step.group}
          today={today}
          onDone={done}
          onCancel={() => setStep({ kind: 'shut' })}
        />
      ) : null}

      <History contact={contact} written={written} />
    </div>
  );
}

type TargetChoice = DoNotContactTarget;

/** Mark her, her number or her address: anybody in the office. */
function SetMark({
  contact,
  today,
  onDone,
  onCancel,
}: {
  contact: ContactRow;
  today: string;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `mark-${contact.id}-${name}`;
  const email = contact.email?.trim() || null;
  const choices: { readonly id: TargetChoice; readonly label: string }[] = [
    { id: 'contact', label: `${contact.displayName}, on every number and address they have` },
    ...(contact.phoneE164
      ? [
          {
            id: 'phone' as const,
            label: `Their number, ${formatPhone(contact.phoneE164)}, whoever holds it`,
          },
        ]
      : []),
    ...(email ? [{ id: 'email' as const, label: `Their address, ${email}` }] : []),
  ];
  const [target, setTarget] = useState<TargetChoice>('contact');
  const [scope, setScope] = useState<SuppressionScope>('all');
  const [reason, setReason] = useState<OfficeMarkReason>('client_request');
  const [day, setDay] = useState(today);
  const [words, setWords] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function mark() {
    if (isAheadOfToday(day, today)) {
      setProblem(
        'A mark is recorded when it is heard, so it cannot carry a day that has not come.',
      );
      return;
    }
    const on: MarkTarget =
      target === 'phone' && contact.phoneE164
        ? { phone: contact.phoneE164 }
        : target === 'email' && email
          ? { email }
          : { contactId: contact.id };
    setBusy(true);
    setProblem(null);
    try {
      const answer = await setMark({
        target: on,
        channel: scope,
        reason,
        note: orNull(words),
        saidAt: capturedAtFor(day, today),
      });
      onDone(
        answer.written
          ? `Marked. Nothing goes to ${contact.displayName} that way from now on.`
          : 'That mark already held. Nothing more was written.',
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/20 p-3">
      <fieldset>
        <legend className="text-sm font-medium">Who it is on</legend>
        <div className="mt-2 flex flex-col gap-2">
          {choices.map((choice) => (
            <label
              key={choice.id}
              htmlFor={field(`target-${choice.id}`)}
              className={`flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3 rounded-lg border p-3 ${
                target === choice.id ? 'border-navy bg-gold/10' : 'border-navy/25'
              }`}
            >
              <input
                id={field(`target-${choice.id}`)}
                type="radio"
                name={field('target')}
                checked={target === choice.id}
                onChange={() => setTarget(choice.id)}
                className="h-5 w-5 shrink-0 accent-navy"
              />
              <span className="text-sm break-words">{choice.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-3">
        <SelectField
          id={field('scope')}
          label="Which way"
          value={scope}
          onChange={setScope}
          options={SUPPRESSION_SCOPES}
          toLabel={(value) => MARK_SCOPE_LABELS[value]}
        />
        <SelectField
          id={field('reason')}
          label="Why"
          value={reason}
          onChange={setReason}
          options={OFFICE_MARK_REASONS}
          toLabel={(value) => DO_NOT_CONTACT_REASON_LABELS[value]}
        />
        <TextField
          id={field('day')}
          label="The day they said it"
          type="date"
          value={day}
          onChange={setDay}
          hint="Today unless this is being written up afterwards."
        />
        <TextAreaField
          id={field('note')}
          label="Their words, or why"
          value={words}
          onChange={setWords}
          rows={2}
          hint="How they asked: on the phone, by text, by email or in person. It stays on the record and never reaches a message."
        />
      </div>

      <p className="mb-3 text-xs text-navy/65">
        A mark on texts withdraws a text still waiting to go to their number, and tells the phone
        system when they asked or the business decided. Only an owner or a manager can lift a mark.
      </p>

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void mark()} disabled={busy}>
          {busy ? 'Writing it down' : 'Mark it'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/**
 * The reasons a stop on texts the office set was passed down to the phone system for, as
 * `suppress_number` (`api/src/schedule/refusals.ts`). Nothing passes a lift down yet: ops asked the
 * phone system how on `faithfully-kept-phone#26`, so it may go on refusing texts to her number after
 * one of these is lifted, until it is told. The door's answer to a lift says whether it was told,
 * which is the truth; this is what the dialog can say before the lift is pressed.
 */
const PASSED_DOWN: ReadonlySet<DoNotContactReason> = new Set<DoNotContactReason>([
  'client_request',
  'owner_decision',
]);

/** Whether the phone system was likely told to refuse her texts for one of these marks. */
const passedDownAmong = (groups: readonly MarkGroup[]): boolean =>
  groups.some(({ mark, channels }) => channels.includes('sms') && PASSED_DOWN.has(mark.reason));

/** What the phone system may still do once a lift lands, said the same way before and after. */
const MAY_STILL_REFUSE = 'until it is told they may go again, which ops cannot do yet';

/**
 * What the phone system does with her own STOP, said the same way before and after. The contract
 * holds it there until she texts START, and she may have already: the office lifts her STOP when the
 * phone system kept her START from ops (ops #62), so the screen never says she has not.
 */
const UNTIL_HER_START = 'until they text START to the business number, if they have not already';

/**
 * The sentence after a lift. The door's answer decides whether the phone system was told to refuse
 * texts to the number; a Worker that predates the answer leaves it to the reasons lifted. Her own
 * STOP is held at the phone system as well, until she texts START there, which the contract says.
 */
function liftedWords(
  contact: ContactRow,
  lifting: readonly MarkGroup[],
  answer: LiftAnswer,
): string {
  const kept = 'Lifted. The mark stays on the record, and the lift beside it.';
  const told = answer.phoneMayStillRefuse;
  if (told) {
    return `${kept} The phone system may still refuse texts to ${formatPhone(told.number) ?? told.number} ${MAY_STILL_REFUSE}.`;
  }
  if (told === undefined && passedDownAmong(lifting)) {
    return `${kept} The phone system may still refuse texts to their number ${MAY_STILL_REFUSE}.`;
  }
  if (
    lifting.some(({ mark, channels }) => channels.includes('sms') && mark.reason === 'carrier_stop')
  ) {
    return `${kept} The phone system refuses texts to their ${UNTIL_HER_START}.`;
  }
  return `${kept} ${contact.displayName} may be told about their cleans that way again, where nothing else refuses it.`;
}

/** Lift one mark, by name: an owner or a manager, and the server says so either way. */
function LiftMark({
  contact,
  group,
  today,
  onDone,
  onCancel,
}: {
  contact: ContactRow;
  group: MarkGroup;
  today: string;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `lift-${group.mark.id}-${name}`;
  // The sets standing beneath the mark, once the door has named them: a lift releases every set on
  // its channel, so it lands only when they are named too, and the owner sees them first.
  const [beneath, setBeneath] = useState<readonly BeneathMark[] | null>(null);
  const lifting: readonly MarkGroup[] = [group, ...(beneath ?? [])];
  // Her STOP at the carrier is her own word, lifted only because she asked for texts again, whether
  // it is the mark named or stands beneath it. A stop on texts passed down beside it is not hers, and
  // her START at the phone system releases her own STOP there and nothing ops pushed, so where one is
  // among them the dialog warns of that instead of pointing to her START.
  const herStop = lifting.some(({ mark }) => mark.reason === 'carrier_stop');
  const passedDown = passedDownAmong(lifting);
  const reasons: readonly LiftReason[] = herStop ? ['came_back'] : LIFT_REASONS;
  const [reason, setReason] = useState<LiftReason>('came_back');
  const [day, setDay] = useState(today);
  const [words, setWords] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function lift() {
    if (isAheadOfToday(day, today)) {
      setProblem(
        'A lift is recorded when it is heard, so it cannot carry a day that has not come.',
      );
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const answer = await liftMark({
        markId: group.mark.id,
        reason,
        note: orNull(words),
        saidAt: capturedAtFor(day, today),
        ...(beneath && beneath.length > 0
          ? { alsoLifting: beneath.map((under) => under.mark.id) }
          : {}),
      });
      onDone(liftedWords(contact, lifting, answer));
    } catch (thrown) {
      const under = beneathOf(thrown);
      if (under) {
        if (beneath !== null) {
          setProblem('What stands beneath this mark changed while you looked. Read it again.');
        }
        setBeneath(under);
        if (under.some(({ mark }) => mark.reason === 'carrier_stop')) setReason('came_back');
        return;
      }
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
      <p className="text-sm">Lift this mark? {markLine(group)}</p>
      {beneath && beneath.length > 0 ? (
        <div className="mt-2 text-sm">
          <p className="font-medium">Beneath this mark, and lifted with it:</p>
          <ul className="mt-1 flex flex-col gap-1">
            {beneath.map((under) => (
              <li key={under.mark.id}>{markLine(under)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {herStop ? (
        <p className="mt-2 text-sm">
          Their STOP at the carrier is their own word. Lift it only because they asked for texts
          again.
          {passedDown ? null : ` The phone system refuses every text to their ${UNTIL_HER_START}.`}
        </p>
      ) : null}
      {passedDown ? (
        <p className="mt-2 text-sm">
          When {lifting.length > 1 ? 'one of these' : 'this'} was marked the phone system was told
          to refuse texts to their number, and a lift is not passed on to it: it may still refuse
          every text to their number {MAY_STILL_REFUSE}.
        </p>
      ) : null}

      <div className="mt-3">
        <SelectField
          id={field('reason')}
          label="Why"
          value={reason}
          onChange={setReason}
          options={reasons}
          toLabel={(value) => DO_NOT_CONTACT_REASON_LABELS[value]}
        />
        <TextField
          id={field('day')}
          label="The day they said it"
          type="date"
          value={day}
          onChange={setDay}
          hint="Today unless this is being written up afterwards."
        />
        <TextAreaField
          id={field('note')}
          label="Their words, or why"
          value={words}
          onChange={setWords}
          rows={2}
          hint="How they asked to hear from us again, or why the mark was wrong. It stays on the record with your name."
        />
      </div>

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void lift()} disabled={busy}>
          {busy ? 'Writing it down' : beneath && beneath.length > 0 ? 'Lift them all' : 'Lift it'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/** Which way a row of the history holds. */
function scopeWords(channel: SuppressionScope): string {
  return channel === 'all' ? 'every way' : CHANNEL_NOUN[channel];
}

/** Who a row of the history is on. */
function onWords(row: MarkHistoryRow): string {
  if (row.targetKind === 'phone')
    return `the number ${formatPhone(row.phoneE164) ?? row.phoneE164}`;
  if (row.targetKind === 'email') return `the address ${row.email}`;
  return 'them';
}

/**
 * EVERY ROW ON HER, HER NUMBER AND HER ADDRESS, newest first, read when somebody opens it: who, when,
 * why and her words. A phone makes no request for it until asked.
 */
function History({ contact, written }: { contact: ContactRow; written: number }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<readonly MarkHistoryRow[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const email = contact.email?.trim() || null;

  useEffect(() => {
    if (!open) return;
    let live = true;
    setRows(null);
    setFailed(null);
    markHistory({ contact: contact.id, phone: contact.phoneE164, email })
      .then((answer) => {
        if (live) setRows(answer.marks);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(thrown instanceof ApiError ? thrown.message : 'The history did not load.');
      });
    return () => {
      live = false;
    };
  }, [open, contact.id, contact.phoneE164, email, written]);

  return (
    <div className="mt-2">
      <Button tone="quiet" onClick={() => setOpen((shown) => !shown)}>
        {open ? 'Hide the history' : 'History of do not contact'}
      </Button>
      {!open ? null : failed ? (
        <p className="mt-2 text-sm" role="alert">
          {failed}
        </p>
      ) : rows === null ? (
        <p className="mt-2 text-sm text-navy/60" role="status">
          Reading the history.
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-sm text-navy/70">Nothing has ever been marked for them.</p>
      ) : (
        <ol className="mt-2 flex flex-col gap-2" aria-label="History of do not contact">
          {rows.map((row) => (
            <li key={row.id} className="text-sm">
              <p>
                <span className="font-medium">{row.kind === 'set' ? 'Set' : 'Lifted'}</span>,{' '}
                {scopeWords(row.channel)}, on {onWords(row)}.{' '}
                {DO_NOT_CONTACT_REASON_LABELS[row.reason]}. {formatDay(dayOf(row.saidAt))},{' '}
                {whoWords(row.recordedByName, row.door)}.
              </p>
              {row.note ? <p className="text-xs text-navy/65">Note: {row.note}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
